import Filter from "bad-words";
import Decimal from "decimal.js";
import { t } from "../../../utils/i18n";

export const validateString = (string) => {
  const filter = new Filter();

  const linkRegex = /(?:https?:\/\/)?(?:www\.)?\S+\.\S+/gi;
  const hasLink = linkRegex.test(string);

  if (hasLink) {
    return t("p2p.helpers.no_links");
  }
};

export const calcfixedPrice = (tradeType, selectedCurrency) => {
  let temFixedPrice;
  if (tradeType === "buy") {
    temFixedPrice =
      selectedCurrency.buyRate != null && !isNaN(selectedCurrency.buyRate)
        ? selectedCurrency.buyRate
        : selectedCurrency.rateToUsd;
  } else if (tradeType === "sell") {
    temFixedPrice =
      selectedCurrency.sellRate != null && !isNaN(selectedCurrency.sellRate)
        ? selectedCurrency.sellRate
        : selectedCurrency.rateToUsd;
  }
  return temFixedPrice;
};

export const formatNumber = (number) => {
  const options = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };

  const formatter = new Intl.NumberFormat("en-US", options);
  return number ? formatter.format(parseFloat(number)) : "0.00";
};

export const handleAmountChange = (e, setRange, setDisplayRate) => {
  let rawValue = e.target.value.replace(/[^0-9]/g, "");
  let [integerPart, decimalPart] = rawValue.split(".");

  integerPart = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(Number(integerPart));

  let formattedValue =
    decimalPart !== undefined ? `${integerPart}.${decimalPart}` : integerPart;

  setDisplayRate(formattedValue);
  setRange(formattedValue.replace(/,/g, ""));
};

export const handleTotalAmountChange = (e, setRange, setDisplayRate) => {
  let rawValue = e.target.value.replace(/[^0-9.]/g, "");
  let [integerPart, decimalPart] = rawValue.split(".");

  integerPart = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(Number(integerPart));

  let formattedValue =
    decimalPart !== undefined ? `${integerPart}.${decimalPart}` : integerPart;

  setDisplayRate(formattedValue);
  setRange(formattedValue.replace(/,/g, ""));
};

export const getTotalAmount = (amount, actualRate, accountBalance) => {
  const amountValue = amount ? parseFloat(amount).toFixed(2) : 0;

  return amount
    ? parseFloat(amountValue) * actualRate
    : accountBalance * actualRate;
};

export function validateAmount(
  isMin,
  amount,
  tradeType,
  accountBalance,
  actualRate,
  rangeMin,
  rangeMax,
  setMinAmountError,
  setMaxAmountError,
  setRangeMin,
  setRangeMax,
  updateParent,
  currency
) {
  const minNumericValue = Number(rangeMin.replace(/,/g, ""));
  const maxNumericValue = Number(rangeMax.replace(/,/g, ""));
  const totalAmount = getTotalAmount(amount, actualRate, accountBalance);

  let error = "";
  let exceedBalanceCondition =
    tradeType === "sell" &&
    (minNumericValue > accountBalance * actualRate ||
      maxNumericValue > totalAmount);
  let fieldName = isMin ? "rangeMin" : "rangeMax";
  let otherNumericValue = isMin ? maxNumericValue : minNumericValue;
  let setError = isMin ? setMinAmountError : setMaxAmountError;
  let setRange = isMin ? setRangeMin : setRangeMax;

  if (!isNaN(otherNumericValue)) {
    if (isMin && minNumericValue > otherNumericValue) {
      error = t("p2p.helpers.min_gt_max");
    } else if (!isMin && maxNumericValue < minNumericValue) {
      error = t("p2p.helpers.max_lt_min");
    }
  }

  // Check if min and max are in a valid range
  if (minNumericValue > maxNumericValue) {
    if (isMin) {
      error = t("p2p.helpers.min_gt_max");
    } else {
      error = t("p2p.helpers.max_lt_min");
    }
  } else {
    setMinAmountError("");
    setMaxAmountError("");
  }

  if (exceedBalanceCondition) {
    error = isMin
      ? t("p2p.helpers.min_exceed_total", { currency: currency?.value })
      : t("p2p.helpers.max_exceed_total", { currency: currency?.value });

    updateParent("amountValError", 1);
  } else {
    updateParent("amountValError", 0);
    setError(error);
  }

  setError(error);
  updateParent("rangeMin", rangeMin);
  updateParent("rangeMax", rangeMax);
}

export const validateTotalAmount = (
  amount,
  accountBalance,
  tradeType,
  tradeFee
) => {
  if (!amount) return t("p2p.helpers.amount_required");

  const formattedAmount = parseFloat(amount.replace(/,/g, ""));
  const formattedAccountBalance = parseFloat(accountBalance);
  const fee = calculateTradeFee(formattedAmount, tradeFee);

  if (formattedAmount < 1) return t("p2p.helpers.min_usd");

  if (
    tradeType === "sell" &&
    formattedAmount + parseFloat(fee) > formattedAccountBalance
  ) {
    return t("p2p.helpers.amount_fee_exceed");
  }

  return "";
};

export const calculateTradeFee = (amount, fee) => {
  if (!amount || !fee) return 0;

  return ((fee / 100) * amount).toFixed(2);
};

export const calculateTotalAmount = (amount, tradeRate) => {
  return parseFloat(amount || "0") * parseFloat(tradeRate || "0");
};

export const getFiatAmount = (marketrate, amount) => {
  const marketRateVal = parseFloat(marketrate);
  const amountInUsd = parseFloat(amount);
  const calculatedMargin = marketRateVal * amountInUsd;
  return calculatedMargin.toFixed(2);
};

export const calculateTraderRate = (newMargin, currActualRate) => {
  const marginVal = newMargin ? new Decimal(Number(newMargin)) : 0;
  const rate = new Decimal(currActualRate);

  const tempAmount = rate.mul(marginVal).div(new Decimal(100));
  let result = rate.plus(tempAmount);
  let roundedResult = Number(result).toFixed(2);

  if (
    Math.abs(Number(roundedResult) - Math.floor(Number(roundedResult))) === 0.99
  ) {
    let steppedResult = Number(roundedResult) + 0.01;
    return steppedResult.toFixed(2);
  }
  return roundedResult;
};

export const formatMinMaxAmount = (amount) => {
  const numericValue = amount.toString();
  return numericValue
    ? new Intl.NumberFormat("en-US", {
        style: "decimal",
        maximumFractionDigits: 2,
      }).format(numericValue)
    : "";
};
